/**
 * Route guards. Both gate on `isPending` rather than `isLoading`, and both
 * wait for `useIsRestoring()`.
 *
 * The reason is the persisted cache (`lib/api/persist.ts`). While
 * `<PersistQueryClientProvider>` reads sessionStorage back, TanStack Query
 * holds every query at `status: "pending"` with `fetchStatus: "idle"` — no
 * request has been allowed to start yet. `isLoading` is `isPending &&
 * isFetching`, so it is *false* for that render: a guard written against it
 * would see "not loading, no user, no error" and bounce a signed-in user to
 * /login on every hard refresh. `isPending` is the honest question here —
 * "do we have an answer yet?" — and `isRestoring` covers the same window
 * explicitly.
 *
 * Waiting here also spares every board below: nothing inside the shell renders
 * during the restore frame, so no panel flashes its empty or unconfigured
 * state on the way to painting from the restored cache.
 */

import { Spinner } from "@norican/react";
import { useIsRestoring } from "@tanstack/react-query";
import { Navigate, Outlet } from "react-router";

import { useMe } from "@/lib/api";

/** Full-height spinner, shown while the session answer is still unknown. */
function RouteSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner size="md" tone="neutral" />
    </div>
  );
}

export function ProtectedRoute() {
  const { data: me, isPending, error } = useMe();
  const isRestoring = useIsRestoring();

  if (isRestoring || isPending) {
    return <RouteSpinner />;
  }

  if (error || !me) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet context={{ me }} />;
}

export function AdminRoute() {
  const { data: me, isPending } = useMe();
  const isRestoring = useIsRestoring();

  if (isRestoring || isPending) {
    return <RouteSpinner />;
  }

  if (!me) {
    return <Navigate to="/login" replace />;
  }

  if (me.role !== "admin") {
    return <Navigate to="/overview" replace />;
  }

  return <Outlet context={{ me }} />;
}
